export interface ChannelSpec {
  label?: string;
  unit?: string;
  decimals?: 0 | 1 | 2;
  format: (stored: number) => number;
  parse: (typed: number) => number;
}

/** Parameter constraint types that are adjustable via an on-canvas gizmo handle. */
export const GIZMO_PARAM_TYPES = ['point', 'point_radius', 'angle_radius', 'line'] as const;

export type GizmoParamType = (typeof GIZMO_PARAM_TYPES)[number];

/** Whether a parameter constraint type is adjustable via an on-canvas gizmo handle. */
export function isGizmoParamType(type: string): type is GizmoParamType {
  return (GIZMO_PARAM_TYPES as readonly string[]).includes(type);
}

const clampPct = (n: number): number => Math.min(Math.max(n, 0), 100);

const position = (label: string): ChannelSpec => ({
  label,
  unit: '%',
  decimals: 0,
  format: (v) => v * 100,
  parse: (n) => clampPct(n) / 100,
});

const radius = (label: string): ChannelSpec => ({
  label,
  unit: '%',
  decimals: 0,
  format: (v) => v * 100,
  parse: (n) => Math.max(n, 0) / 100,
});

const angle: ChannelSpec = {
  label: 'a',
  unit: '°',
  decimals: 0,
  format: (v) => (v * 180) / Math.PI,
  parse: (n) => (n * Math.PI) / 180,
};

export function gizmoChannels(type: GizmoParamType): ChannelSpec[] {
  switch (type) {
    case 'point':
      return [position('x'), position('y')];
    case 'point_radius':
      return [position('x'), position('y'), radius('r')];
    case 'angle_radius':
      return [position('x'), position('y'), radius('r'), angle];
    case 'line':
      return [position('x1'), position('y1'), position('x2'), position('y2')];
  }
}

/** One sub-row of the right-panel field. */
export interface ChannelGroup {
  /** Indices into this type's {@link gizmoChannels}. */
  indices: number[];
  /** Appended to the parameter's label to head the row, e.g. `Ripple radius`. */
  labelSuffix?: string;
}

/** How {@link GizmoVectorField} splits a type's channels into rows. */
export function gizmoChannelGroups(type: GizmoParamType): ChannelGroup[] {
  if (type === 'angle_radius') {
    return [{ indices: [0, 1] }, { indices: [2], labelSuffix: 'radius' }, { indices: [3], labelSuffix: 'angle' }];
  }
  return [{ indices: gizmoChannels(type).map((_, i) => i) }];
}
